import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { t as tx } from "@/lib/i18n";
import { useAuth } from "@/contexts/AuthContext";

const AppContext = createContext(null);

export function AppProvider({ children }) {
  const { user } = useAuth();
  const [lang, setLang] = useState(() => localStorage.getItem("lang") || "el");
  const [theme, setTheme] = useState(() => localStorage.getItem("theme") || "dark");
  const [cases, setCases] = useState([]);
  const [currentCaseId, setCurrentCaseId] = useState(() => localStorage.getItem("caseId") || null);

  // Apply theme
  useEffect(() => {
    const root = document.documentElement;
    if (theme === "dark") root.classList.add("dark");
    else root.classList.remove("dark");
    localStorage.setItem("theme", theme);
  }, [theme]);

  useEffect(() => { localStorage.setItem("lang", lang); }, [lang]);
  useEffect(() => { document.documentElement.lang = lang; }, [lang]);
  useEffect(() => { if (currentCaseId) localStorage.setItem("caseId", currentCaseId); }, [currentCaseId]);

  // sync from user profile on first load
  useEffect(() => {
    if (user && typeof user === "object") {
      if (user.language && !localStorage.getItem("lang")) setLang(user.language);
      if (user.theme && !localStorage.getItem("theme")) setTheme(user.theme);
    }
  }, [user]);

  const loadCases = useCallback(async () => {
    if (!user || typeof user !== "object") return [];
    const { data } = await api.get("/cases");
    setCases(data || []);
    if (!currentCaseId && data && data.length > 0) setCurrentCaseId(data[0].id);
    return data;
  }, [user, currentCaseId]);

  useEffect(() => {
    if (user && typeof user === "object") loadCases();
  }, [user, loadCases]);

  const t = useCallback((key, vars) => tx(lang, key, vars), [lang]);

  const currentCase = cases.find(c => c.id === currentCaseId) || null;

  return (
    <AppContext.Provider value={{
      lang, setLang, theme, setTheme,
      t, cases, setCases, currentCaseId, setCurrentCaseId,
      currentCase, loadCases,
    }}>
      {children}
    </AppContext.Provider>
  );
}

export const useApp = () => useContext(AppContext);
